"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";

type Props = {
  threshold?: string;
};

export function ScrollObserver({ threshold = "10vh" }: Props) {
  const pathname = usePathname();
  const observerRef = useRef<IntersectionObserver>(null);
  const metaRef = useRef<HTMLMetaElement | null>(null);
  const originalColorRef = useRef<string | null>(null);

  useEffect(() => {
    // Reset meta theme color on route change
    if (metaRef.current && originalColorRef.current) {
      metaRef.current.setAttribute("content", originalColorRef.current);
    }
  }, [pathname]);

  return (
    <div
      className="observer absolute"
      style={{ top: threshold }}
      aria-hidden="true"
      ref={(el) => {
        if (!el) return;

        metaRef.current = document.querySelector('meta[name="theme-color"]');
        originalColorRef.current =
          metaRef.current?.getAttribute("content") ?? null;

        // Get background color from CSS variable
        const getBackgroundColor = () => {
          const bgValue = getComputedStyle(document.documentElement)
            .getPropertyValue("--background")
            .trim();
          return `hsl(${bgValue})`;
        };

        observerRef.current = new IntersectionObserver(
          ([entry]) => {
            el.classList.toggle("intersecting", !entry.isIntersecting);

            if (metaRef.current && originalColorRef.current) {
              metaRef.current.setAttribute(
                "content",
                !entry.isIntersecting
                  ? getBackgroundColor()
                  : originalColorRef.current
              );
            }
          },
          { threshold: 1 }
        );

        observerRef.current.observe(el);
        return () => {
          observerRef.current?.disconnect();
          // Reset only if we had a meta tag and original color
          if (metaRef.current && originalColorRef.current) {
            metaRef.current.setAttribute("content", originalColorRef.current);
          }
        };
      }}
    />
  );
}
